import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FormLabel } from "@/components/ui/form";
import { useRiderPayrollEstimate } from "@/hooks/use-rider-payroll-estimate";
import { cn, formatCurrency } from "@/lib/utils";
import { RiderSaleDetailDialog } from "@/pages/rider-sales/rider-sale-detail-dialog";

import type { RiderSale } from "@artisancode/api-types";

export function RiderPayrollPeriodPicker({
  riderId,
  payrollId,
  selectedIds,
  onChangeSelected,
  readOnly,
}: {
  riderId: string;
  /** Id of the payroll being edited, or null when creating a new one. */
  payrollId: string | null;
  selectedIds: string[];
  onChangeSelected: (ids: string[]) => void;
  readOnly: boolean;
}) {
  const [viewingSale, setViewingSale] = useState<RiderSale | null>(null);

  const { sales, salesLoaded, dailyWageRate, grossBeforeAdjustments } =
    useRiderPayrollEstimate(riderId, selectedIds);

  // "Selesai" = rider_sale status done/paid — same set the backend counts as
  // a worked day. A sale is pickable here if it's unlinked, or already
  // linked to the payroll we're editing — sales linked to a different
  // payroll are already spoken for and simply don't show up.
  const candidateSales = useMemo(() => {
    return sales
      .filter((s) => s.status === "done" || s.status === "paid")
      .filter((s) => s.payrollId === null || s.payrollId === payrollId)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [sales, payrollId]);

  const hydrated = useRef(false);
  useEffect(() => {
    hydrated.current = false;
  }, [payrollId]);
  useEffect(() => {
    if (!payrollId || !salesLoaded || hydrated.current) return;
    hydrated.current = true;
    onChangeSelected(
      candidateSales.filter((s) => s.payrollId === payrollId).map((s) => s.id),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate once per payrollId, guarded by the ref above
  }, [payrollId, salesLoaded]);

  function dailyPay(s: RiderSale) {
    return dailyWageRate + s.totalSalesIncome + s.totalBonus;
  }

  const selectedSales = candidateSales.filter((s) =>
    selectedIds.includes(s.id),
  );
  const selectedDates = [...new Set(selectedSales.map((s) => s.date))].sort();

  function handlePickAll() {
    onChangeSelected(candidateSales.map((s) => s.id));
  }

  function toggle(id: string) {
    onChangeSelected(
      selectedIds.includes(id)
        ? selectedIds.filter((d) => d !== id)
        : [...selectedIds, id],
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <FormLabel>Periode</FormLabel>
        {!readOnly && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!riderId || candidateSales.length === 0}
            onClick={handlePickAll}
          >
            Ambil Semua Penjualan Belum Dibayar
          </Button>
        )}
      </div>

      <p className="text-sm">
        {selectedDates.length > 0 ? (
          <>
            Periode:{" "}
            <span className="font-medium">
              {selectedDates[0]} s/d {selectedDates[selectedDates.length - 1]}
            </span>
            <span className="text-muted-foreground">
              {" "}
              ({selectedDates.length} hari kerja ·{" "}
              {formatCurrency(grossBeforeAdjustments)})
            </span>
          </>
        ) : (
          <span className="text-muted-foreground">
            Belum ada penjualan dipilih.
          </span>
        )}
      </p>

      {!readOnly && riderId && (
        <div className="max-h-56 overflow-y-auto rounded-lg border divide-y">
          {candidateSales.length === 0 ? (
            <p className="p-3 text-xs text-muted-foreground">
              Tidak ada penjualan rider berstatus "done" yang belum dibayar.
            </p>
          ) : (
            candidateSales.map((s) => {
              const checked = selectedIds.includes(s.id);
              return (
                <label
                  key={s.id}
                  className="flex cursor-pointer items-center gap-2.5 p-2 text-xs hover:bg-muted/40"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(s.id)}
                  />
                  <span
                    className={cn(
                      "flex-1",
                      !checked && "text-muted-foreground",
                    )}
                  >
                    {s.date}
                  </span>
                  <span className="text-muted-foreground">
                    {s.totalSoldQty} unit terjual
                    {s.totalBonus > 0 &&
                      ` · Bonus Rp${s.totalBonus.toLocaleString("id-ID")}`}
                  </span>
                  <span className="shrink-0 font-medium">
                    {formatCurrency(dailyPay(s))}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setViewingSale(s);
                    }}
                    className="shrink-0 text-primary underline-offset-2 hover:underline"
                  >
                    Detail
                  </button>
                </label>
              );
            })
          )}
        </div>
      )}

      <RiderSaleDetailDialog
        sale={viewingSale}
        onOpenChange={(next) => !next && setViewingSale(null)}
      />
    </div>
  );
}
